import { useState } from "react";
import {
  Plus,
  RefreshCw,
  Pencil,
  Trash2,
  Loader2,
  Search,
  ExternalLink,
  Star,
  LayoutGrid,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAdsSpyStore } from "../store/adsSpyStore";
import { adsSpyService } from "../services/adsSpy.service";
import { timeAgo, countryFlag } from "../utils/helpers";
import { SCRAPE_STATUS, type SpyBrand } from "../types/adsSpy.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface BrandsTableProps {
  onOpenBrandModal: (brand?: SpyBrand) => void;
  onViewBrandAds: (brandId: number) => void;
}

const AVATAR_COLORS = [
  { bg: "#EEF0FF", text: "#5050EA" },
  { bg: "#FEF3C7", text: "#D97706" },
  { bg: "#DCFCE7", text: "#16A34A" },
  { bg: "#FFE4E6", text: "#E11D48" },
  { bg: "#E0E7FF", text: "#4F46E5" },
  { bg: "#DBEAFE", text: "#2563EB" },
  { bg: "#F3E8FF", text: "#9333EA" },
  { bg: "#CCFBF1", text: "#0D9488" },
];

function getAvatarColor(id: number) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

export default function BrandsTable({
  onOpenBrandModal,
  onViewBrandAds,
}: BrandsTableProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const { brands, brandsLoading, loadBrands, loadAds } = useAdsSpyStore();
  const [scrapingBrand, setScrapingBrand] = useState<number | null>(null);

  const handleScrape = async (brand: SpyBrand) => {
    setScrapingBrand(brand.id);
    toast(t("searching-ads-for", "Searching ads for {name}...").replace("{name}", brand.name));
    try {
      await adsSpyService.scrapeBrand(brand.id);
      const poll = setInterval(async () => {
        try {
          const status = await adsSpyService.getScrapeStatus(brand.id);
          if (status.status === SCRAPE_STATUS.COMPLETED) {
            clearInterval(poll);
            toast.success(t("search-completed-for", "Search completed for {name}").replace("{name}", brand.name));
            setScrapingBrand(null);
            await loadBrands();
            useAdsSpyStore.getState().setFilter("brand_id", "");
            await loadAds(true);
          } else if (status.status === SCRAPE_STATUS.FAILED) {
            clearInterval(poll);
            toast.error(t("error-searching-ads-for", "Error searching ads for {name}").replace("{name}", brand.name));
            setScrapingBrand(null);
          }
        } catch {
          // ignore polling errors
        }
      }, 5000);
    } catch (err) {
      toast.error(t("error-searching-ads-for", "Error searching ads for {name}").replace("{name}", brand.name));
      setScrapingBrand(null);
      console.error(err);
    }
  };

  const handleDelete = async (brand: SpyBrand) => {
    if (!confirm(t("confirm-delete-brand", "Delete \"{name}\" and all its unsaved ads?").replace("{name}", brand.name)))
      return;
    try {
      await adsSpyService.deleteBrand(brand.id);
      toast.success(t("brand-deleted", "Brand deleted"));
      await loadBrands();
    } catch (err) {
      toast.error(t("error-deleting-brand", "Error deleting brand"));
      console.error(err);
    }
  };

  const isNewBrand = (brand: SpyBrand) =>
    brand.ad_count === 0 && !brand.last_scraped_at;

  if (brandsLoading && brands.length === 0) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-[#5050EA]" />
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-28 bg-white rounded-2xl border border-gray-200">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-[#EEF0FF]">
          <Search className="w-6 h-6 text-[#5050EA]" />
        </div>
        <p className="text-base font-semibold text-gray-900">
          {t("no-brands-yet", "You don't have brands yet")}
        </p>
        <p className="text-sm mt-2 mb-7 text-gray-400 text-center max-w-xs leading-relaxed">
          {t("add-first-competitor", "Add your first competitor to start spying on their ads")}
        </p>
        <button
          onClick={() => onOpenBrandModal()}
          className="text-[13px] font-semibold px-5 py-2.5 rounded-full text-white flex items-center gap-2 shadow-sm hover:shadow-md transition-all duration-200"
          style={{ background: "linear-gradient(to right, #5050EA, #7B7BF7)" }}
        >
          <Plus className="w-4 h-4" />
          {t("add-brand", "Add Brand")}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className="grid items-center px-5 py-2.5 bg-gray-50/80 border-b border-gray-200"
        style={{ gridTemplateColumns: "1.5fr 0.6fr 0.8fr 1fr 2fr", gap: "0 12px" }}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t("brand-table-brand", "Brand")}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">{t("brand-table-ads", "Ads")}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">{t("brand-table-bookmarks", "Bookmarks")}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t("brand-table-activity", "Activity")}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">{t("brand-table-actions", "Actions")}</span>
      </div>

      {/* Rows */}
      {brands.map((brand) => {
        const avatar = getAvatarColor(brand.id);
        const hasAds = brand.ad_count > 0;
        const neverScraped = isNewBrand(brand);
        const isScraping = scrapingBrand === brand.id;

        return (
          <div
            key={brand.id}
            className="grid items-center px-5 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors duration-100"
            style={{ gridTemplateColumns: "1.5fr 0.6fr 0.8fr 1fr 2fr", gap: "0 12px" }}
          >
            {/* Brand */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0"
                style={{ background: avatar.bg, color: avatar.text }}
              >
                {brand.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-900 truncate">
                    {brand.name}
                  </span>
                  {brand.country && (
                    <span className="shrink-0 text-xs leading-none">{countryFlag(brand.country)}</span>
                  )}
                  <a
                    href={brand.ad_library_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 text-gray-300 hover:text-[#5050EA] transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <span className="text-[11px] text-gray-400 leading-tight">{brand.category}</span>
              </div>
            </div>

            {/* Ads */}
            <div className="text-center">
              {hasAds ? (
                <button
                  onClick={() => onViewBrandAds(brand.id)}
                  className="text-[13px] font-semibold text-[#5050EA] hover:underline tabular-nums"
                >
                  {brand.ad_count}
                </button>
              ) : (
                <span className="text-[13px] text-gray-300 tabular-nums">0</span>
              )}
            </div>

            {/* Favoritos */}
            <div className="text-center">
              {brand.bookmark_count > 0 ? (
                <span className="inline-flex items-center justify-center gap-1 text-[13px] font-semibold text-amber-500 tabular-nums">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  {brand.bookmark_count}
                </span>
              ) : (
                <span className="text-[13px] text-gray-300 tabular-nums">0</span>
              )}
            </div>

            {/* Actividad */}
            <div>
              {brand.last_scraped_at ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                  <span className="text-[11px] text-gray-500">{timeAgo(brand.last_scraped_at)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                  <span className="text-[11px] text-gray-400">{t("no-activity", "No activity")}</span>
                </div>
              )}
            </div>

            {/* ── Actions toolbar ── */}
            <div className="flex items-center justify-end gap-2">
              {/* Grupo 1: Operativo — [Primary CTA] + [Buscar nuevos] */}
              <div className="flex items-center gap-1.5">
                {neverScraped ? (
                  <button
                    onClick={() => handleScrape(brand)}
                    disabled={isScraping}
                    className="text-[11px] font-semibold px-3.5 py-1.5 rounded-lg bg-[#5050EA] text-white hover:bg-[#4545D8] disabled:opacity-50 flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    {isScraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    {t("search-ads", "Search ads")}
                  </button>
                ) : (
                  <>
                    {hasAds && (
                      <button
                        onClick={() => onViewBrandAds(brand.id)}
                        className="text-[11px] font-semibold px-3.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center gap-1.5"
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                        {t("view-ads", "View ads")}
                      </button>
                    )}
                    <button
                      onClick={() => handleScrape(brand)}
                      disabled={isScraping}
                      className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      {isScraping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      {t("search-new", "Search new")}
                    </button>
                  </>
                )}
              </div>

              {/* Divisor vertical */}
              <div className="h-5 w-px bg-gray-200 mx-1" />

              {/* Grupo 2: Admin — [Edit] [Delete] con tooltip custom */}
              <div className="flex items-center gap-0.5">
                <div className="relative group/edit">
                  <button
                    onClick={() => onOpenBrandModal(brand)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[10px] font-medium text-white bg-gray-800 rounded-md whitespace-nowrap opacity-0 group-hover/edit:opacity-100 transition-opacity duration-150 pointer-events-none">
                    {t("edit", "Edit")}
                  </span>
                </div>
                <div className="relative group/delete">
                  <button
                    onClick={() => handleDelete(brand)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[10px] font-medium text-white bg-gray-800 rounded-md whitespace-nowrap opacity-0 group-hover/delete:opacity-100 transition-opacity duration-150 pointer-events-none">
                    {t("delete", "Delete")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
